import React from 'react';
import styled, { keyframes } from 'styled-components';
import { CheckCircle, Clock, AlertCircle, FileText, Shield, User, Mail, Calendar } from 'lucide-react';

// Animations
const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  background: #f8fafc;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
`;

const Header = styled.div`
  background: white;
  border-bottom: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
`;

const HeaderContent = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const IconContainer = styled.div`
  padding: 0.75rem;
  background: #dbeafe;
  border-radius: 0.75rem;
  border: 1px solid #bfdbfe;
`;

const TitleSection = styled.div``;

const Title = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
`;

const Subtitle = styled.p`
  color: #64748b;
  margin: 0.25rem 0 0 0;
  font-size: 1rem;
`;

const HeaderGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
`;

const HeaderCard = styled.div`
  background: #f8fafc;
  border-radius: 0.5rem;
  padding: 1rem;
  border: 1px solid #e2e8f0;
`;

const HeaderCardLabel = styled.p`
  font-size: 0.875rem;
  font-weight: 500;
  color: #64748b;
  margin: 0 0 0.25rem 0;
`;

const HeaderCardValue = styled.p`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  margin: 0;
`;

const TimelineContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 3rem 1.5rem;
`;

const TimelineWrapper = styled.div`
  position: relative;
`;

const TimelineLine = styled.div`
  position: absolute;
  left: 2rem;
  top: 0;
  bottom: 0;
  width: 2px;
  background: linear-gradient(to bottom, #3b82f6 0%, #d1d5db 50%, #3b82f6 100%);
  border-radius: 1px;
`;

const TimelineStages = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const StageContainer = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 2rem;
  animation: ${fadeIn} 0.6s ease-out;
`;

const TimelineNode = styled.div`
  flex-shrink: 0;
  width: 4rem;
  height: 4rem;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  position: relative;
  z-index: 10;
  border: 2px solid;
  transition: all 0.3s ease;
  
  ${props => {
    switch (props.status) {
      case 'completed':
        return `
          background: #10b981;
          border-color: #a7f3d0;
          box-shadow: 0 4px 6px -1px rgba(16, 185, 129, 0.2);
        `;
      case 'current':
        return `
          background: #3b82f6;
          border-color: #bfdbfe;
          box-shadow: 0 4px 6px -1px rgba(59, 130, 246, 0.2);
        `;
      default:
        return `
          background: #9ca3af;
          border-color: #e5e7eb;
          box-shadow: 0 4px 6px -1px rgba(156, 163, 175, 0.2);
        `;
    }
  }}
  
  &:hover {
    transform: scale(1.05);
  }
`;

const StageContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const StageCard = styled.div`
  background: white;
  border-radius: 0.5rem;
  border: 1px solid #e2e8f0;
  padding: 1.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    border-color: #cbd5e1;
  }
`;

const StageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
  
  @media (min-width: 1024px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const StageTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0;
  
  ${props => {
    switch (props.status) {
      case 'completed':
        return 'color: #059669;';
      case 'current':
        return 'color: #2563eb;';
      default:
        return 'color: #64748b;';
    }
  }}
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 600;
  border: 2px solid;
  
  ${props => {
    switch (props.status) {
      case 'completed':
        return `
          background: #ecfdf5;
          color: #059669;
          border-color: #a7f3d0;
        `;
      case 'current':
        return `
          background: #eff6ff;
          color: #2563eb;
          border-color: #bfdbfe;
        `;
      default:
        return `
          background: #f8fafc;
          color: #64748b;
          border-color: #e2e8f0;
        `;
    }
  }}
`;

const AssignedSection = styled.div`
  background: #eff6ff;
  border-radius: 0.5rem;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
  border: 1px solid #bfdbfe;
`;

const AssignedHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
`;

const AssignedIconContainer = styled.div`
  padding: 0.5rem;
  background: #dbeafe;
  border-radius: 0.5rem;
  border: 1px solid #bfdbfe;
`;

const AssignedLabel = styled.span`
  font-weight: 600;
  color: #1e293b;
`;

const AssignedName = styled.p`
  color: #1e293b;
  font-weight: 600;
  margin: 0 0 0.75rem 0;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ContactLink = styled.a`
  color: #2563eb;
  font-weight: 500;
  text-decoration: none;
  transition: color 0.2s ease;
  
  &:hover {
    color: #1d4ed8;
  }
`;

const ContactText = styled.span`
  color: #374151;
  font-weight: 500;
`;

const LogsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const LogsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const LogIndicator = styled.div`
  width: 4px;
  height: 1rem;
  background: #9ca3af;
  border-radius: 2px;
`;

const LogsTitle = styled.h4`
  font-weight: 700;
  color: #1e293b;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0;
`;

const LogItem = styled.div`
  border-radius: 0.5rem;
  padding: 1rem;
  border: 1px solid;
  
  ${props => {
    switch (props.type) {
      case 'success':
        return `
          color: #065f46;
          background: #ecfdf5;
          border-color: #a7f3d0;
        `;
      case 'warning':
        return `
          color: #92400e;
          background: #fffbeb;
          border-color: #fcd34d;
        `;
      default:
        return `
          color: #1e40af;
          background: #eff6ff;
          border-color: #bfdbfe;
        `;
    }
  }}
`;

const LogContent = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
`;

const LogMessage = styled.p`
  font-size: 0.875rem;
  font-weight: 500;
  flex: 1;
  margin: 0;
`;

const LogTimestamp = styled.span`
  font-size: 0.75rem;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-weight: 600;
  white-space: nowrap;
  background: rgba(255, 255, 255, 0.6);
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  border: 1px solid rgba(0, 0, 0, 0.1);
`;

const Footer = styled.div`
  margin-top: 3rem;
  background: white;
  border-radius: 0.5rem;
  border: 1px solid #e2e8f0;
  padding: 2rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
`;

const FooterContent = styled.div`
  text-align: center;
`;

const FooterHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const FooterIconContainer = styled.div`
  padding: 0.5rem;
  background: #dbeafe;
  border-radius: 0.5rem;
  border: 1px solid #bfdbfe;
`;

const FooterTitle = styled.p`
  color: #1e293b;
  font-weight: 700;
  font-size: 1.25rem;
  margin: 0;
`;

const FooterDescription = styled.p`
  color: #64748b;
  margin: 0 0 1.5rem 0;
  max-width: 32rem;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
`;

const FooterActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.5rem;
  
  @media (min-width: 640px) {
    flex-direction: row;
  }
`;

const SupportLink = styled.a`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #2563eb;
  text-decoration: none;
  background: #eff6ff;
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  border: 2px solid #bfdbfe;
  font-weight: 600;
  transition: all 0.2s ease;
  
  &:hover {
    color: #1d4ed8;
    border-color: #93c5fd;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
`;

const SupportStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #059669;
  background: #ecfdf5;
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  border: 1px solid #a7f3d0;
`;

const StatusDot = styled.div`
  width: 0.5rem;
  height: 0.5rem;
  background: #10b981;
  border-radius: 50%;
  animation: ${pulse} 2s infinite;
`;

const SupportText = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
`;

function Timeline() {
  // Mock data - this would come from server response
  const assessmentData = {
    assessmentId: 'VAPT-2024-001',
    clientName: 'TechCorp Solutions',
    startDate: '2024-01-15',
    estimatedCompletion: '2024-02-15',
    currentStage: 'testing-in-progress'
  };

  const timelineStages = [
    {
      id: 'waiting-approval',
      title: 'Waiting for Approval',
      status: 'completed',
      icon: <Clock className="w-5 h-5" />,
      logs: [
        {
          id: '1',
          timestamp: '2024-01-15 09:30 AM',
          message: 'Assessment request submitted successfully',
          type: 'info'
        },
        {
          id: '2',
          timestamp: '2024-01-15 02:15 PM',
          message: 'Scan approved by Admin (admin@zerodayops.in)',
          type: 'success'
        },
        {
          id: '3',
          timestamp: '2024-01-15 02:30 PM',
          message: 'Assessment scope validated and confirmed',
          type: 'success'
        }
      ]
    },
    {
      id: 'testing-in-progress',
      title: 'Testing In Progress',
      status: 'current',
      icon: <Shield className="w-5 h-5" />,
      assignedTo: 'Sarah Johnson - Senior Penetration Tester',
      contactEmail: 'sarah.johnson@zerodayops.in',
      estimatedCompletion: '2024-02-01',
      logs: [
        {
          id: '4',
          timestamp: '2024-01-16 10:00 AM',
          message: 'Professional pentester Sarah Johnson has been assigned to your assessment',
          type: 'info'
        },
        {
          id: '5',
          timestamp: '2024-01-16 11:30 AM',
          message: 'Initial reconnaissance and asset discovery completed',
          type: 'success'
        },
        {
          id: '6',
          timestamp: '2024-01-17 02:00 PM',
          message: 'Vulnerability scanning initiated across all target systems',
          type: 'info'
        },
        {
          id: '7',
          timestamp: '2024-01-18 04:45 PM',
          message: 'Manual penetration testing in progress - 15 potential vulnerabilities identified',
          type: 'warning'
        }
      ]
    },
    {
      id: 'reporting',
      title: 'Reporting',
      status: 'pending',
      icon: <FileText className="w-5 h-5" />,
      logs: [
        {
          id: '8',
          timestamp: 'Pending',
          message: 'Comprehensive report generation will begin after testing completion',
          type: 'info'
        }
      ]
    },
    {
      id: 'quality-check',
      title: 'Quality Check',
      status: 'pending',
      icon: <AlertCircle className="w-5 h-5" />,
      logs: [
        {
          id: '9',
          timestamp: 'Pending',
          message: 'Senior security analyst will review all findings and recommendations',
          type: 'info'
        }
      ]
    },
    {
      id: 'completed',
      title: 'Completed',
      status: 'pending',
      icon: <CheckCircle className="w-5 h-5" />,
      logs: [
        {
          id: '10',
          timestamp: 'Pending',
          message: 'Final report and executive summary will be delivered',
          type: 'info'
        }
      ]
    }
  ];

  return (
    <Container>
      {/* Header */}
      <Header>
        <HeaderContent>
          <HeaderTitle>
            <IconContainer>
              <Shield className="w-8 h-8 text-blue-600" />
            </IconContainer>
            <TitleSection>
              <Title>VAPT Assessment Timeline</Title>
              <Subtitle>Real-time tracking of your security assessment</Subtitle>
            </TitleSection>
          </HeaderTitle>
          
          <HeaderGrid>
            <HeaderCard>
              <HeaderCardLabel>Assessment ID</HeaderCardLabel>
              <HeaderCardValue>{assessmentData.assessmentId}</HeaderCardValue>
            </HeaderCard>
            <HeaderCard>
              <HeaderCardLabel>Client</HeaderCardLabel>
              <HeaderCardValue>{assessmentData.clientName}</HeaderCardValue>
            </HeaderCard>
            <HeaderCard>
              <HeaderCardLabel>Estimated Completion</HeaderCardLabel>
              <HeaderCardValue>{assessmentData.estimatedCompletion}</HeaderCardValue>
            </HeaderCard>
          </HeaderGrid>
        </HeaderContent>
      </Header>

      {/* Timeline */}
      <TimelineContainer>
        <TimelineWrapper>
          {/* Timeline Line */}
          <TimelineLine />
          
          {/* Timeline Stages */}
          <TimelineStages>
            {timelineStages.map((stage, index) => (
              <StageContainer key={stage.id}>
                {/* Timeline Node */}
                <TimelineNode status={stage.status}>
                  {stage.icon}
                </TimelineNode>

                {/* Stage Content */}
                <StageContent>
                  <StageCard>
                    <StageHeader>
                      <StageTitle status={stage.status}>
                        {stage.title}
                      </StageTitle>
                      <StatusBadge status={stage.status}>
                        {stage.status === 'completed' ? 'Completed' : 
                         stage.status === 'current' ? 'In Progress' : 'Pending'}
                      </StatusBadge>
                    </StageHeader>

                    {/* Assigned Team Member */}
                    {stage.assignedTo && (
                      <AssignedSection>
                        <AssignedHeader>
                          <AssignedIconContainer>
                            <User className="w-5 h-5 text-blue-600" />
                          </AssignedIconContainer>
                          <AssignedLabel>Assigned Team Member</AssignedLabel>
                        </AssignedHeader>
                        <AssignedName>{stage.assignedTo}</AssignedName>
                        <ContactInfo>
                          {stage.contactEmail && (
                            <ContactItem>
                              <Mail className="w-4 h-4 text-slate-600" />
                              <ContactLink href={`mailto:${stage.contactEmail}`}>
                                {stage.contactEmail}
                              </ContactLink>
                            </ContactItem>
                          )}
                          {stage.estimatedCompletion && (
                            <ContactItem>
                              <Calendar className="w-4 h-4 text-slate-600" />
                              <ContactText>Est. Completion: {stage.estimatedCompletion}</ContactText>
                            </ContactItem>
                          )}
                        </ContactInfo>
                      </AssignedSection>
                    )}

                    {/* Status Logs */}
                    <LogsSection>
                      <LogsHeader>
                        <LogIndicator />
                        <LogsTitle>Status Logs</LogsTitle>
                      </LogsHeader>
                      {stage.logs.map((log) => (
                        <LogItem key={log.id} type={log.type}>
                          <LogContent>
                            <LogMessage>{log.message}</LogMessage>
                            <LogTimestamp>
                              {log.timestamp}
                            </LogTimestamp>
                          </LogContent>
                        </LogItem>
                      ))}
                    </LogsSection>
                  </StageCard>
                </StageContent>
              </StageContainer>
            ))}
          </TimelineStages>
        </TimelineWrapper>

        {/* Footer */}
        <Footer>
          <FooterContent>
            <FooterHeader>
              <FooterIconContainer>
                <Shield className="w-6 h-6 text-blue-600" />
              </FooterIconContainer>
              <FooterTitle>
                Need assistance with your assessment?
              </FooterTitle>
            </FooterHeader>
            <FooterDescription>
              Our security experts are available 24/7 to answer questions and provide updates on your vulnerability assessment and penetration testing process.
            </FooterDescription>
            <FooterActions>
              <SupportLink href="mailto:support@zerodayops.in">
                <Mail className="w-5 h-5" />
                support@zerodayops.in
              </SupportLink>
              <SupportStatus>
                <StatusDot />
                <SupportText>24/7 Support Available</SupportText>
              </SupportStatus>
            </FooterActions>
          </FooterContent>
        </Footer>
      </TimelineContainer>
    </Container>
  );
}

export default Timeline;